import * as React from "react";
import { Drawer } from "../../Drawer";
import styles from "./FilterDrawer.module.scss";
import { Indicator } from "../Indicator/Indicator";
import { Button, ButtonIcon } from "../../buttons";
import {
  ComponentProps,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useToast } from "../../Toast";
import * as RadixCheckbox from "@radix-ui/react-checkbox/dist";
import { Checkbox } from "../../checkbox";
import { RadioGroupItem } from "../../radio";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { SingleSelect, SingleSelectProps } from "../SingleSelect/SingleSelect";
import { useControllableState } from "../../../helpers/state/state-hooks";
import { useTranslation } from "react-i18next";
import { IconException } from "../../icons";
import { useRangeFilters } from "../RangeFilter/RangeFilter";
import { IconSlider3 } from "../../icons";
import { Input, InputProps } from "../../input";
import { MultiFilter, MultiFilterProps } from "../MultiFilter/MultiFilter";
import { MultiSelect, MultiSelectProps } from "../MultiSelect/MultiSelect";
import { useIsMobile } from "../../../helpers/is-mobile/isMobile";

export type FilterDrawerProps = {
  /**The children to be rendered in the main section of the drawer,
   * children can be wrapped in AllFilters.Item in order to apply the correct styling and padding*/
  children?: React.ReactNode;
  /**A callback function that is fired when the apply button is clicked,
   * if the logic to apply these filters was successful, successfulApplyCallback can be invoked to display a success toast*/
  onApply: (successfulApplyCallback: () => void) => void;
  /**A callback function that is fired when the clear all button is clicked*/
  onClearAll: () => void;
  /**The number of filters currently applied,
   * any value larger than 0 will display a badge to the right of the button text.
   * Any value larger than 99 will be modified to display 99+
   */
  appliedFilters: number;
  /** A callback function that is fired when the drawer is closed directly through user input.
   * This includes clicking the close button, clicking outside of the drawer, or pressing the escape key
   */
  onUserClose?: () => void;
  /**Whether the apply button is disabled*/
  applyDisabled?: boolean;
  /**Whether the clear button is disabled*/
  clearDisabled?: boolean;
  /** Is the drawer disabled? */
  disabled?: boolean;
  /** A callback that is emitted whenever the open state of the drawer changes*/
  onOpenChange?: (open: boolean) => void;
  /** Whether to focus on the first input field upon opening the drawer.
   * @default true*/
  focusOnFirstInput?: boolean;
};

/**
 * A filter button that opens a side drawer on the right hand side of the screen.
 * The number of active filters will be displayed as a badge next to the button text.
 * Implementation of the clear, cancel, and apply logic is required by the developer.
 *
 * Users are notified of filters being successfully applied via Toast, as such,
 * the Toaster component is required as a top level provider in the application.
 */
export const FilterDrawer = ({
  children,
  onApply,
  onClearAll,
  appliedFilters,
  applyDisabled = false,
  onUserClose,
  disabled = false,
  onOpenChange,
  focusOnFirstInput = true,
}: FilterDrawerProps) => {
  const { t } = useTranslation("common");
  const toast = useToast();
  const ref = useRef<HTMLSpanElement>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const isMobile = useIsMobile();
  const handleApplyButtonClick = (): void => {
    setOpen(false);
    onApply(handleSuccessfulFilterApply);
  };

  const handleClearAllButtonClick = (): void => {
    onClearAll();
  };

  const handleSuccessfulFilterApply = (): void => {
    toast.success({ body: "Filters applied", sensitivity: "foreground" });
  };

  const [open, setOpen] = React.useState(false);
  const handleOpenChange = () => setOpen(!open);

  useEffect(() => {
    onOpenChange && onOpenChange(open);
  }, [open]);
  const responsiveFooter = (
    <div className={styles.footer}>
      <Button
        disabled={applyDisabled}
        className={styles.footerButton}
        onClick={handleApplyButtonClick}
        variant="primary"
      >
        Apply
      </Button>
      <Button
        onClick={handleClearAllButtonClick}
        className={styles.footerButton}
        variant="tertiary-blue"
        data-testid="filter-clear"
      >
        {t("CLEAR")}
      </Button>
    </div>
  );

  useEffect(() => {
    if (!ref.current) return;
    setWidth(ref.current.clientWidth);
    setHeight(ref.current.clientHeight);
  }, [ref]);

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={handleOpenChange}
        footer={responsiveFooter}
        title="All Filters"
        closeOnOutsideClick
        closeOnEscapeKeyPress
        onUserClose={() => onUserClose && onUserClose()}
        onOpenAutoFocus={(e) => !focusOnFirstInput && e.preventDefault()}
      >
        {children}
      </Drawer>

      {isMobile ? (
        <>
          <div className={styles.mobileContainer}>
            <ButtonIcon onClick={handleOpenChange} variant="tertiary-black">
              <IconSlider3 />
            </ButtonIcon>
            {appliedFilters > 0 && (
              <Indicator
                className={styles.indicatorMobile}
                count={appliedFilters}
                aria-label={`${appliedFilters} filter${
                  appliedFilters > 1 ? "s" : ""
                } applied`}
              />
            )}
          </div>
        </>
      ) : (
        <Button
          className={styles.button}
          onClick={handleOpenChange}
          data-testid="btn-all-filters"
          disabled={disabled}
          data-open={open}
        >
          {/*Always include the bolded version of the text so that the button*/}
          {/*does not change width when active  */}
          <span className={styles.buttonLabelMaxWidth} ref={ref} aria-hidden>
            All filters
          </span>
          <div className={styles.buttonLabelContainer}>
            <span className={styles.buttonLabel}>All filters</span>
          </div>
          {appliedFilters > 0 && (
            <Indicator
              count={appliedFilters}
              aria-label={`${appliedFilters} filter${
                appliedFilters > 1 ? "s" : ""
              } applied`}
            />
          )}
        </Button>
      )}
    </>
  );
};

export interface FilterDrawerItemProps {
  label?: string;
  children: ReactNode;
}

const FilterDrawerItem = (props: FilterDrawerItemProps) => {
  const { label, children } = props;

  return (
    <div className={styles.allFiltersItemContainer}>
      {label}
      <div className={styles.allFiltersItem}>{children}</div>
    </div>
  );
};

FilterDrawer.Item = FilterDrawerItem;

export interface FilterDrawerCheckboxProps extends RadixCheckbox.CheckboxProps {
  label: string;
  indeterminate?: boolean;
}

const FilterDrawerCheckbox = (props: FilterDrawerCheckboxProps) => {
  const { label, ...rest } = props;

  return (
    <div className={styles.allFiltersCheckbox}>
      <Checkbox {...rest} />
      <label htmlFor={rest.id} aria-label={label} aria-disabled={rest.disabled}>
        {label}
      </label>
    </div>
  );
};

FilterDrawer.Checkbox = FilterDrawerCheckbox;

const FilterDrawerSingleSelect = (
  props: PropsWithChildren<SingleSelectProps>,
) => {
  const { value, onChange, children, ...propsToPass } = props;
  const [selectedValue, setSelectedValue] = useControllableState({
    prop: value,
    onChange: onChange,
    defaultProp: null,
  });

  const handleClearClick = () => {
    setSelectedValue(null);
  };

  return (
    <div className={styles.select}>
      <SingleSelect
        {...propsToPass}
        value={selectedValue}
        onChange={setSelectedValue}
        filterClassName={styles.filter}
        dropdownClassName={styles.selectDropdown}
        includePortal={false}
      >
        {children}
      </SingleSelect>
      <Button
        type="button"
        variant="tertiary-blue"
        onClick={handleClearClick}
        disabled={!selectedValue}
      >
        Reset
      </Button>
    </div>
  );
};

FilterDrawerSingleSelect.Option = SingleSelect.Option;
FilterDrawerSingleSelect.Delimiter = SingleSelect.Delimiter;
FilterDrawer.SingleSelect = FilterDrawerSingleSelect;

const FilterDrawerMultiSelect = (
  props: PropsWithChildren<MultiSelectProps>,
) => {
  const { value, onChange, children, ...propsToPass } = props;
  const [selectedValue, setSelectedValue] = useControllableState({
    prop: value,
    onChange: onChange,
    defaultProp: [],
  });
  const [open, setOpen] = useState(false);

  const handleClearClick = () => {
    setSelectedValue([]);
  };

  return (
    <div className={styles.select}>
      <MultiSelect
        {...propsToPass}
        value={selectedValue}
        onChange={setSelectedValue}
        open={props.open ?? open}
        onOpenChange={setOpen}
        filterClassName={styles.filter}
        dropdownClassName={styles.selectDropdown}
        includePortal={false}
      >
        {children}
      </MultiSelect>
      <Button
        type="button"
        variant="tertiary-blue"
        onClick={handleClearClick}
        disabled={!selectedValue}
      >
        Reset
      </Button>
    </div>
  );
};

FilterDrawerMultiSelect.Option = MultiSelect.Option;
FilterDrawerMultiSelect.Delimiter = MultiSelect.Delimiter;
FilterDrawerMultiSelect.SelectAll = MultiSelect.SelectAll;
FilterDrawer.MultiSelect = FilterDrawerMultiSelect;

export interface FilterDrawerRadioProps extends RadioGroup.RadioGroupItemProps {
  label: string;
}

const FilterDrawerRadio = (props: FilterDrawerRadioProps) => {
  const { label, ...rest } = props;

  return (
    <div className={styles.allFiltersCheckbox}>
      <RadioGroupItem {...rest} />
      <label
        htmlFor={props.id}
        aria-label={label}
        aria-disabled={props.disabled}
      >
        {label}
      </label>
    </div>
  );
};

FilterDrawer.Radio = FilterDrawerRadio;

export interface FilterDrawerRangeFilterProps extends ComponentProps<"div"> {
  /** The method that will be called when the range values change. */
  onValueChange: (emittedValues?: {
    min: number | null;
    max: number | null;
  }) => void;
  /** The text to be displayed on the right of each range input. */
  valueLabel?: string;
  /** Is the filter disabled? */
  disabled?: boolean;
  /** The default minimum value. */
  defaultMin?: number;
  /** The default maximum value. */
  defaultMax?: number;
  /** The minimum value, if it is externally managed */
  currentMin?: number | null;
  /** The maximum value, if it is externally managed */
  currentMax?: number | null;
}

const FilterDrawerRangeFilter = (props: FilterDrawerRangeFilterProps) => {
  const {
    minRange,
    maxRange,
    handleRangeChange,
    onRangeResetClick,
    showError,
    errorCondition,
    lastInputted,
    isResetButtonDisabled,
  } = useRangeFilters(
    props.onValueChange,
    props.defaultMin,
    props.defaultMax,
    props.disabled,
    false,
    props.currentMin,
    props.currentMax,
  );

  const preventFormSubmission = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const minError = "The min must be less than the max";
  const maxError = "The max must be greater than the min";

  // text label to display on the right of each range input box
  const valueLabel = <p>{props.valueLabel}</p>;

  return (
    <div className={styles.rangeFilter}>
      <div className={styles.rangeContainer}>
        <div className={styles.rangeMenuItem}>
          <p className={styles.rangeDescriptionText}>Min</p>
          <input
            className={`${styles.rangeInput} ${
              errorCondition && showError && lastInputted === "min"
                ? styles.inputError
                : ""
            }`}
            data-testid="min-input"
            type={"number"}
            onChange={(event) => {
              handleRangeChange(event, "min");
            }}
            key={"min"}
            value={minRange !== null ? minRange : ""}
            disabled={props.disabled}
            onKeyDown={preventFormSubmission}
          />
          <span className={styles.valueLabel}>{valueLabel}</span>
        </div>

        <div className={styles.rangeMenuItem}>
          <p className={styles.rangeDescriptionText}>Max</p>
          <input
            className={`${styles.rangeInput} ${
              errorCondition && showError && lastInputted === "max"
                ? styles.inputError
                : ""
            }`}
            data-testid="max-input"
            type={"number"}
            onChange={(event) => {
              handleRangeChange(event, "max");
            }}
            key={"max"}
            value={maxRange !== null ? maxRange : ""}
            disabled={props.disabled}
            onKeyDown={preventFormSubmission}
          />
          <span className={styles.valueLabel}>{valueLabel}</span>
        </div>
      </div>

      {errorCondition && showError ? (
        <div className={styles.errorContainer}>
          <IconException />
          <span className={styles.errorText}>
            {lastInputted === "min" ? minError : maxError}
          </span>
        </div>
      ) : (
        <></>
      )}

      <Button
        onClick={onRangeResetClick}
        disabled={isResetButtonDisabled}
        variant="tertiary-blue"
        type="button"
      >
        Reset
      </Button>
    </div>
  );
};

FilterDrawer.RangeFilter = FilterDrawerRangeFilter;

export interface FilterDrawerInputProps extends InputProps {
  label: string;
  onClear: () => void;
}

const FilterDrawerInput = (props: FilterDrawerInputProps) => {
  const { label, onClear, ...rest } = props;

  return (
    <div className={styles.allFiltersInput}>
      <Input {...rest} />

      <Button
        variant="tertiary-blue"
        className={styles.allFiltersInputReset}
        disabled={!rest.value}
        onClick={onClear}
      >
        Reset
      </Button>
    </div>
  );
};

FilterDrawer.Input = FilterDrawerInput;

export interface FilterDrawerMultiFilterProps extends MultiFilterProps {}

const FilterDrawerMultiFilter = (props: FilterDrawerMultiFilterProps) => {
  const { ...rest } = props;

  return (
    <div className={styles.allFiltersMultiSelect}>
      <MultiFilter {...rest} asComponentOnly={true} />

      <Button
        variant="tertiary-blue"
        className={styles.allFiltersInputReset}
        onClick={() => props.onChange && props.onChange([])}
      >
        Reset
      </Button>
    </div>
  );
};

FilterDrawer.MultiFilter = FilterDrawerMultiFilter;
